import { Component, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatterStatementOfAccount,
	StatementOfAccountDefaultTypes,
	StatementOfAccountType,
} from '@models/statementOfAccount';
import { MatterStatementOfAccountOverview } from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterStatementofAccountSelectors } from '@state/matter/statementOfAccounts/statementOfAccounts.selectors';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { StatementOfAccountTotals } from '../../../shared/services/financialTotals/financial-totals.service';
import { ICON } from '@core/util/icon';
import {
	MatterStatementOfAccountEditDocumentModalData,
	MatterStatementOfAccountsEditDocumentTextComponent,
} from './edit-document-text/edit-document-text.component';
import {
	AddMatterStatementOfAccountComponent,
	AddStatementOfAccountData,
} from './add/add.component';
import { fade, openClose } from '@core/animations/animations';
import { cloneDeep } from 'lodash';
import { InputComponent } from 'src/app/shared/atomic/atoms/input/input.component';
import { BusinessState } from '@state/business';
import { UrlService } from '@services/url/url.service';
import { MatterFileType, TeranetDocket } from '@models/matter';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { MatterWordDocumentsDefaultsSource } from '@state/matter/matter-word-documents/matter-word-documents-defaults-source.selectors';
import { TeranetService } from '@services/teranet/teranet.service';
import { ResetSoAccPriceOverrides } from '@state/matter/matter.state';
import { TitleSearchFeeSettings } from '@models/titleSearch';
import { ModalService } from '@ui/modal/modal.service';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { ResetModalComponent } from './reset-modal/reset-modal.component';

const stringifyCompare = (prev, curr): boolean =>
	JSON.stringify(prev) === JSON.stringify(curr);

@UntilDestroy()
@Component({
	selector: 'app-accounts',
	templateUrl: './accounts.component.html',
	animations: [fade(), openClose()],

	standalone: false,
})
export class AccountsComponent {
	public statementsFee: MatterStatementOfAccount[];
	public statementsHst: MatterStatementOfAccount[];
	public statementsNonHst: MatterStatementOfAccount[];
	public totals: StatementOfAccountTotals;
	public statementTypes = StatementOfAccountType;
	public statementOfAccounts: MatterStatementOfAccountOverview;
	public ICON = ICON;

	@Select(SelectedMatter.useHtmlDocuments)
	public useHtmlDocuments$: Observable<boolean>;
	@ViewSelectSnapshot(SelectedMatter.statementOfAccountAllInclusivePrice)
	public allInclusivePrice: number;

	@ViewSelectSnapshot(SelectedMatter.statementOfAccountUseAllInclusivePrice)
	public useInclusivePrice: boolean;

	@ViewSelectSnapshot(SelectedMatter.matterHST) public hst: number;
	@ViewSelectSnapshot(SelectedMatter.statementOfAccount)
	public soa: MatterStatementOfAccountOverview;

	@SelectSnapshot(SelectedMatter.id) public matterId: string;
	@SelectSnapshot(SelectedMatter.matterFileType)
	public fileType: MatterFileType;
	@Select(MatterStatementofAccountSelectors.statementOfAccountsTotals)
	public totals$: Observable<StatementOfAccountTotals>;

	@Select(MatterStatementofAccountSelectors.getAll) public items$: Observable<
		MatterStatementOfAccount[]
	>;

	@Select(SelectedMatter.getResettingSOA)
	public resetting$: Observable<boolean>;

	@ViewChild('recievedInput', { read: InputComponent, static: false })
	recievedInput?: InputComponent;

	@ViewSelectSnapshot(BusinessState.isAccountingEnabled)
	isAccountingEnabled: boolean;

	@Select(
		MatterWordDocumentsDefaultsSource.getStatementOfAccountDocumentSourceIdByMatterType,
	)
	statementOfAccountDocSourceIdByMatterType$: Observable<string>;

	@Select(BusinessState.getShowWordSideBySide) showWordSideBySide$: boolean;

	@SelectSnapshot(SelectedMatter.createdAt) matterCreatedAt: number;
	@SelectSnapshot(SelectedMatter.matterHST) matterHST: number;
	@SelectSnapshot(SelectedMatter.getTitleSearchFeeSettings)
	titleSearchFeeSettings: TitleSearchFeeSettings | undefined;
	@SelectSnapshot(SelectedMatter.teranetDocket) teranetDocket: TeranetDocket;

	public accountingUrl: string;

	constructor(
		private matDialog: MatDialog,
		private matterDispatch: MatterDispatch,
		private analytics: AnalyticsService,
		private teranetService: TeranetService,
		private modalService: ModalService,
	) {
		this.accountingUrl = UrlService.Matter_Closing_Accounting_Cosmolex(
			this.matterId,
		);
		this.totals$
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged<StatementOfAccountTotals>(stringifyCompare),
			)
			.subscribe((totals) => {
				this.totals = totals;
			});

		this.items$.pipe(untilDestroyed(this)).subscribe((_items) => {
			const items = cloneDeep(_items);
			if (items?.length > 0) {
				items?.sort((prev, curr) => prev.index - curr.index);
				this.statementsFee = items.filter(
					(statement) => statement.type === StatementOfAccountType.FEE,
				);
				this.statementsHst = items.filter(
					(statement) => statement.type === StatementOfAccountType.HST,
				);
				this.statementsNonHst = items.filter(
					(statement) => statement.type === StatementOfAccountType.NON_HST,
				);
			}
		});
	}

	addStatement(type: StatementOfAccountType): void {
		const data: AddStatementOfAccountData = {
			index: this.statementTypeIndex(type),
			title: this.statementTypeTitle(type),
			type,
		};

		this.matDialog.open(AddMatterStatementOfAccountComponent, { data });
	}

	editStatementText(statement: MatterStatementOfAccount) {
		const data: MatterStatementOfAccountEditDocumentModalData = {
			statement,
		};

		this.matDialog.open(MatterStatementOfAccountsEditDocumentTextComponent, {
			data,
		});
	}

	setUseInclusivePrice($event) {
		this.matterDispatch.Update(this.matterId, {
			soaUseAllInclusivePrice: $event,
		});
	}

	setAllInclusivePrice($event) {
		this.matterDispatch.Update(this.matterId, { soaAllInclusivePrice: $event });
	}

	updateReceived(ev: number | null): void {
		this.matterDispatch.SetStatementOfAccountsReceived(this.matterId, ev);
	}

	async reset() {
		this.analytics.logEvent(
			AnalyticsEvent.STATEMENT_OF_ACCOUNT_RESET_BUTTON_CLICK,
		);
		this.modalService.openModal(ResetModalComponent, {
			title: 'Restore Default Values',
			size: 'sm',
			type: UI_TYPES.DANGER,
			primaryAction: {
				text: 'Reset',
				action: async () => {
					this.modalService.closeModal();
					await this.resetSOA();
				},
			},
			secondaryAction: {
				text: 'Cancel',
				action: () => {
					this.modalService.closeModal();
				},
			},
		});
	}

	private async resetSOA() {
		this.matterDispatch.UpdateResetSOAState(true);

		const priceOverrides: ResetSoAccPriceOverrides = {};

		if (this.teranetDocket?.docketTeranetSystemId) {
			const docketSummaries = await this.teranetService.getDocketSummaries({
				docket_system_id: this.teranetDocket.docketTeranetSystemId,
				createdAt: this.matterCreatedAt,
				matterHST: this.matterHST,
			});

			if (!('errorMessage' in docketSummaries)) {
				const processedDocketSummaries =
					this.teranetService.getSummariesByTitleSearchSettings(
						docketSummaries,
						this.titleSearchFeeSettings,
					);

				priceOverrides[StatementOfAccountDefaultTypes.HST_TITLE] =
					processedDocketSummaries.parcel.total_hst_appl;
				priceOverrides[StatementOfAccountDefaultTypes.NON_HST_TITLE] =
					processedDocketSummaries.parcel.total_hst_not_appl;
				priceOverrides[StatementOfAccountDefaultTypes.HST_WRIT] =
					processedDocketSummaries.writs.total_hst_appl;
				priceOverrides[StatementOfAccountDefaultTypes.NON_HST_WRIT] =
					processedDocketSummaries.writs.total_hst_not_appl;
			}
		}

		this.matterDispatch.ResetSOA(this.matterId, this.fileType, priceOverrides);
	}

	private statementTypeIndex(type: StatementOfAccountType): number {
		let statements: MatterStatementOfAccount[] = [];

		switch (type) {
			case StatementOfAccountType.FEE:
				statements = this.statementsFee;
				break;
			case StatementOfAccountType.HST:
				statements = this.statementsHst;
				break;
			case StatementOfAccountType.NON_HST:
				statements = this.statementsNonHst;
				break;
		}

		return Math.max(...statements.map((statement) => statement.index)) + 1;
	}

	private statementTypeTitle(type: StatementOfAccountType): string {
		switch (type) {
			case StatementOfAccountType.FEE:
				return 'Legal fees';
			case StatementOfAccountType.HST:
				return 'HST Disbursements';
			case StatementOfAccountType.NON_HST:
				return 'Non HST Disbursements';
		}
	}
}
